import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import { Box, Button, IconButton, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { TOAST_STATE } from "../../const";
import { useLanguage } from "../../hooks/useLanguage";
import { userActions } from "../../store";
import { triggerToast } from "../../utils";
import { DASHBOARD_PALETTE as dash } from "../../utils/palette.token";

const menuPaperSx = {
	mt: 1.25,
	borderRadius: 2,
	border: `1px solid ${dash.cardBorder}`,
	background: dash.gradientSurface,
	backgroundImage: "none",
	boxShadow: `0 20px 50px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(100, 160, 255, 0.08)`,
	overflow: "hidden",
};

const menuItemSx = {
	color: dash.textPrimary,
	fontSize: "0.8125rem",
	py: 1.25,
	px: 2,
	gap: 1.5,
	fontFamily: "Montserrat600, Montserrat, sans-serif",
	"&:hover": { bgcolor: dash.rowInset },
	"&.Mui-selected": {
		bgcolor: dash.rowInset,
		color: dash.accent,
	},
	"&.Mui-selected:hover": {
		bgcolor: dash.rowInset,
	},
};

export const Header = () => {
	const dispatch = useDispatch();
	const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
	const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
	const { language, setLanguage } = useLanguage();

	const handleLogout = () => {
		dispatch(userActions.updateUserAuthState(false));
		triggerToast("Logout successfully", TOAST_STATE.Success);
		setUserAnchor(null);
	};

	return (
		<Box
			className="flex justify-between items-center w-full"
			sx={{ color: dash.textPrimary }}
		>
			<Box
				component="h1"
				className="font-raleway700"
				sx={{ fontSize: "20px", m: 0, fontWeight: 700 }}
			>
				Dashboard
			</Box>

			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Button
					variant="text"
					onClick={(e) => setLangAnchor(e.currentTarget)}
					aria-haspopup="menu"
					aria-expanded={Boolean(langAnchor)}
					sx={{
						color: dash.textPrimary,
						textTransform: "none",
						fontWeight: 600,
						fontFamily: "Montserrat600, Montserrat, sans-serif",
						fontSize: "0.8125rem",
						px: 1.75,
						py: 0.75,
						borderRadius: 2,
						border: `1px solid rgba(110, 160, 220, 0.22)`,
						"&:hover": {
							bgcolor: "rgba(100, 190, 230, 0.06)",
							borderColor: "rgba(110, 190, 235, 0.35)",
						},
						'&[aria-expanded="true"]': {
							borderColor: "rgba(110, 200, 240, 0.4)",
						},
					}}
				>
					{language === "en" ? "English" : "Deutsch"}
				</Button>
				<Menu
					anchorEl={langAnchor}
					open={Boolean(langAnchor)}
					onClose={() => setLangAnchor(null)}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					slotProps={{
						paper: {
							elevation: 0,
							sx: { ...menuPaperSx, minWidth: 168 },
						},
					}}
					disableScrollLock
				>
					<MenuItem
						selected={language === "en"}
						onClick={() => {
							setLanguage("en");
							setLangAnchor(null);
						}}
						sx={menuItemSx}
					>
						English
					</MenuItem>
					<MenuItem
						selected={language === "ger"}
						onClick={() => {
							setLanguage("ger");
							setLangAnchor(null);
						}}
						sx={menuItemSx}
					>
						Deutsch
					</MenuItem>
				</Menu>

				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pl: 0.5 }}>
					<Box
						component="img"
						src="/resource/images/default-avatar.png"
						alt=""
						sx={{
							width: 38,
							height: 38,
							borderRadius: "50%",
							border: `2px solid ${dash.cardBorder}`,
							objectFit: "cover",
							boxShadow: `0 0 0 1px rgba(100, 160, 255, 0.12)`,
						}}
					/>
					<IconButton
						size="small"
						aria-label="Account menu"
						onClick={(e) => setUserAnchor(e.currentTarget)}
						sx={{
							color: dash.accent,
							border: `1px solid ${dash.cardBorder}`,
							bgcolor: "rgba(100, 190, 230, 0.06)",
							"&:hover": { bgcolor: dash.accentDim },
						}}
					>
						<ExpandMoreIcon
							sx={{
								fontSize: 20,
								transition: "transform 0.2s ease",
								transform: userAnchor ? "rotate(180deg)" : "none",
							}}
						/>
					</IconButton>
				</Box>
				<Menu
					anchorEl={userAnchor}
					open={Boolean(userAnchor)}
					onClose={() => setUserAnchor(null)}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					slotProps={{
						paper: {
							elevation: 0,
							sx: { ...menuPaperSx, minWidth: 200 },
						},
					}}
					disableScrollLock
				>
					<MenuItem
						onClick={handleLogout}
						sx={{
							...menuItemSx,
							justifyContent: "space-between",
							minWidth: 200,
						}}
					>
						<Box component="span" sx={{ fontWeight: 600 }}>
							Logout
						</Box>
						<LogoutIcon sx={{ fontSize: 18, color: dash.accent }} />
					</MenuItem>
				</Menu>
			</Box>
		</Box>
	);
};
